import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatIDR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAppText } from "@/contexts/AppTextContext";
import InstallAppCard from "@/components/InstallAppCard";
import AccessCodeGate from "@/components/AccessCodeGate";
import { BookOpen, PlayCircle, TrendingUp, Sparkles, ArrowRight, FileCheck, CheckCircle, Clock, Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const toAbsUrl = (url) => url && !url.startsWith("http") ? `${BACKEND_URL}${url}` : url;
const ROLE_LABEL = { admin: "Admin", coach: "Coach", mentor: "Mentor", student: "Siswa" };

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useAppText();
  const [enrollments, setEnrollments] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [e, c, s] = await Promise.all([
          api.get("/me/enrollments"),
          api.get("/courses"),
          api.get("/me/submissions"),
        ]);
        setEnrollments(e.data);
        setCatalog(c.data);
        setSubmissions(s.data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const enrolledIds = new Set(enrollments.map((e) => e.course.course_id));
  const recommended = catalog.filter((c) => !enrolledIds.has(c.course_id)).slice(0, 6);
  const avgProgress = enrollments.length
    ? Math.round(enrollments.reduce((s, e) => s + e.progress_percent, 0) / enrollments.length)
    : 0;

  const gradedSubmissions = submissions.filter(s => s.grade != null);
  const avgGrade = gradedSubmissions.length
    ? Math.round(gradedSubmissions.reduce((s, x) => s + x.grade, 0) / gradedSubmissions.length)
    : null;

  const firstName = user?.name?.split(" ")[0] || "Sobat";
  const greetingTpl = t("dashboard.greeting", { name: "__NAME__" });
  const [before, after] = greetingTpl.split("__NAME__");

  const recentSubmissions = submissions.slice(0, 4);

  if (error) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
      <p className="text-[#8B8794] mb-4">Gagal memuat dashboard. Periksa koneksi internet kamu.</p>
      <button onClick={() => window.location.reload()} className="btn-primary">Coba Lagi</button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="dashboard-page">
      <AccessCodeGate />
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1A1422]">
          {before}<span className="text-[#FF8C00]">{firstName}</span>{after}
        </h1>
        <p className="text-[#5C5868] mt-1">{t("dashboard.subtitle")}</p>
      </div>

      <InstallAppCard />

      {/* Stats */}
      <div className="grid sm:grid-cols-4 gap-4 mb-10">
        <StatCard icon={BookOpen} label={t("dashboard.stat_enrolled")} value={enrollments.length} testid="stat-enrolled" />
        <StatCard icon={PlayCircle} label={t("dashboard.stat_lessons")} value={enrollments.reduce((s, e) => s + e.completed_lessons.length, 0)} testid="stat-lessons" />
        <StatCard icon={TrendingUp} label={t("dashboard.stat_progress")} value={`${avgProgress}%`} testid="stat-progress" />
        <StatCard icon={Star} label="Nilai rata-rata" value={avgGrade !== null ? avgGrade : "—"} testid="stat-grade" />
      </div>

      {/* Tugas Saya */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-[#6B21A8]" />
            <h2 className="font-heading text-xl font-bold text-[#1A1422]">Tugas Saya</h2>
          </div>
          {submissions.length > 0 && (
            <Link to="/grades" className="text-sm text-[#FF8C00] hover:underline flex items-center gap-1">
              <Star className="w-4 h-4" /> Lihat semua nilai <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        {loading ? (
          <div className="text-[#5C5868] text-sm">Memuat...</div>
        ) : submissions.length === 0 ? (
          <div className="card-surface p-6 text-center text-sm text-[#8B8794]">
            Belum ada tugas yang dikumpulkan.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {recentSubmissions.map((s) => (
              <div key={s.submission_id} className="card-surface p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="text-sm font-semibold text-[#1A1422] line-clamp-1">{s.lesson_title}</div>
                  {s.grade != null ? (
                    <span className="text-xs bg-[#10B981]/10 text-[#10B981] px-2 py-0.5 rounded-full font-bold whitespace-nowrap flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> {s.grade}/100
                    </span>
                  ) : (
                    <span className="text-xs bg-[#F59E0B]/10 text-[#F59E0B] px-2 py-0.5 rounded-full font-bold whitespace-nowrap flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Menunggu
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#8B8794] mb-2">
                  Dikumpulkan {new Date(s.submitted_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                </div>
                {s.feedback && (
                  <div className="bg-[#F4F2F8] rounded-xl p-3 mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      {s.graded_by_picture && (
                        <img src={toAbsUrl(s.graded_by_picture)} alt="" className="w-5 h-5 rounded-full object-cover" />
                      )}
                      <span className="text-xs font-semibold text-[#5C5868]">{s.graded_by_name || "BisaUsaha"}</span>
                      <span className="text-[10px] text-[#8B8794]">{s.graded_by_role && `· ${ROLE_LABEL[s.graded_by_role] || s.graded_by_role}`}</span>
                    </div>
                    <p className="text-xs text-[#1A1422] italic">"{s.feedback}"</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* My courses */}
      <section className="mb-12">
        <h2 className="font-heading text-2xl font-bold text-[#1A1422] mb-4">{t("dashboard.my_courses_title")}</h2>
        {loading ? (
          <div className="text-[#5C5868]">{t("dashboard.loading")}</div>
        ) : enrollments.length === 0 ? (
          <div className="card-surface p-8 text-center" data-testid="empty-enrollments">
            <Sparkles className="w-10 h-10 text-[#FF8C00] mx-auto mb-3" />
            <p className="text-[#5C5868] mb-4">{t("dashboard.empty_text")}</p>
            <Link to="/catalog" className="btn-primary">{t("dashboard.empty_button")}</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...enrollments].sort((a, b) => {
              const pA = parseInt(a.course.title);
              const pB = parseInt(b.course.title);
              const numA = isNaN(pA) ? 999 : pA;
              const numB = isNaN(pB) ? 999 : pB;
              return numA - numB;
            }).map((e) => (
              <Link
                to={`/courses/${e.course.course_id}/learn`}
                key={e.enrollment_id}
                className="card-surface card-hover overflow-hidden block"
                data-testid={`enrollment-card-${e.course.course_id}`}
              >
                <div className="h-36 bg-[#F4F2F8] relative">
                  {e.course.thumbnail && <img src={toAbsUrl(e.course.thumbnail)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-bold text-[#1A1422] mb-2 line-clamp-1">{e.course.title}</h3>
                  <div className="flex items-center justify-between text-xs text-[#5C5868] mb-2">
                    <span>{e.completed_lessons.length}/{e.lesson_count} {t("dashboard.lessons_suffix")}</span>
                    <span className="text-[#FF8C00] font-semibold">{e.progress_percent}%</span>
                  </div>
                  <div className="h-1.5 bg-[#F4F2F8] rounded-full overflow-hidden">
                    <div className="h-full bg-[#FF8C00] transition-all" style={{ width: `${e.progress_percent}%` }} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Recommended */}
      {recommended.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-bold text-[#1A1422]">{t("dashboard.recommended_title")}</h2>
            <Link to="/catalog" className="text-sm text-[#FF8C00] hover:underline flex items-center gap-1" data-testid="see-all-catalog">
              {t("dashboard.see_all")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {recommended.map((c) => (
              <Link to={`/courses/${c.course_id}`} key={c.course_id} className="card-surface card-hover overflow-hidden block" data-testid={`recommend-card-${c.course_id}`}>
                <div className="h-36 bg-[#F4F2F8] relative">
                  {c.thumbnail && <img src={toAbsUrl(c.thumbnail)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-bold text-[#1A1422] mb-2 line-clamp-1">{c.title}</h3>
                  <p className="text-sm text-[#5C5868] line-clamp-2 mb-3">{c.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[#FF8C00] font-bold">{formatIDR(c.price)}</span>
                    <span className="text-xs text-[#5C5868]">{c.lesson_count} {t("dashboard.lessons_suffix")}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-surface p-6 flex items-center gap-4" data-testid={testid}>
      <div className="w-12 h-12 rounded-2xl bg-[#FF8C00]/10 flex items-center justify-center">
        <Icon className="w-6 h-6 text-[#FF8C00]" />
      </div>
      <div>
        <div className="text-2xl font-heading font-extrabold text-[#1A1422]">{value}</div>
        <div className="text-xs text-[#5C5868]">{label}</div>
      </div>
    </div>
  );
}
